import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import usePublicStore from '../../hooks/usePublicStore';
import ChatMessage from './ChatMessage';
import StoreStaffChatTab from './StoreStaffChatTab';
import { getBackendErrorMessage } from '../../utils/toastError';

const StoreChatTab = ({ storeSlug, onNewCustomerMessage, onNewStaffMessage }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [error, setError] = useState(null);
  const [isStaff, setIsStaff] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);
  const [activeStoreTab, setActiveStoreTab] = useState('staff'); // 'clients' | 'staff'
  const [hasNewClientMsg, setHasNewClientMsg] = useState(false);
  const [hasNewStaffMsg, setHasNewStaffMsg] = useState(false);
  const messagesEndRef = useRef(null);

  const { socket } = useSocket();
  const { user } = useAuth();
  const { data: storeData, isLoading: storeLoading, error: storeError } = usePublicStore(storeSlug);

  const store = storeData?.store || null;
  const storeId = store?.id || null;

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Resetear conversación cuando cambia de tienda
  const lastStoreIdRef = useRef(storeId);
  useEffect(() => {
    if (storeId && lastStoreIdRef.current !== storeId) {
      setMessages([]);
      setConversation(null);
      setError(null);
      setInputMessage('');
    }
    lastStoreIdRef.current = storeId;
  }, [storeId]);

  // Si deja de ser staff, forzar pestaña Clientes
  useEffect(() => {
    if (!isStaff && activeStoreTab === 'staff') {
      setActiveStoreTab('clients');
    }
  }, [isStaff, activeStoreTab]);

  // Detectar si el usuario actual es staff de esta tienda
  useEffect(() => {
    if (!storeId || !user) {
      setIsStaff(false);
      setStaffError(null);
      return;
    }

    let cancelled = false;
    const fetchStaffInfo = async () => {
      try {
        setStaffLoading(true);
        setStaffError(null);
        const response = await axios.get(`/api/store/${storeId}/staff/me`, {
          __suppress403Toast: true,
        });
        if (cancelled) return;
        if (response && response.data) {
          setIsStaff(true);
        } else {
          setIsStaff(false);
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404 || status === 403) {
          // No es staff de esta tienda
          setIsStaff(false);
          setStaffError(null);
        } else {
          setIsStaff(false);
          setStaffError(getBackendErrorMessage(err, 'Error al verificar rol de staff para esta tienda.'));
        }
      } finally {
        if (!cancelled) {
          setStaffLoading(false);
        }
      }
    };

    fetchStaffInfo();

    return () => {
      cancelled = true;
    };
  }, [storeId, user]);

  // Cargar conversación y mensajes del cliente al abrir/cambiar de tienda
  useEffect(() => {
    if (!storeId || !user || (isStaff && activeStoreTab === 'staff')) {
      return;
    }

    let cancelled = false;

    const fetchConversation = async () => {
      try {
        setError(null);
        const response = await axios.get(`/api/store/messaging/${storeId}/customer/conversation`, {
          params: { limit: 200 }
        });
        if (cancelled) return;

        const conv = response?.data?.conversation || null;
        const rows = Array.isArray(response?.data?.messages) ? response.data.messages : [];

        setConversation(conv);
        setMessages(
          rows.map((m) => ({
            id: m.id,
            message: m.message,
            author_type: m.author_type,
            created_at: m.created_at,
            username:
              m.author_type === 'staff'
                ? m.staff_name || m.staff_username || store?.name || 'Tienda'
                : m.customer_name || m.customer_username || user.display_name || user.username || 'Tú',
            isOwn: m.author_type !== 'staff',
            metadata: m.metadata
          }))
        );
        scrollToBottom();
      } catch (err) {
        if (cancelled) return;
        setError(getBackendErrorMessage(err, 'No se pudo cargar el historial del chat.'));
      }
    };

    fetchConversation();

    return () => {
      cancelled = true;
    };
  }, [storeId, user, isStaff, activeStoreTab, store]);

  // Escuchar respuestas del staff para este usuario y tienda
  useEffect(() => {
    if (!socket || !user) return;

    const handleStaffMessage = (payload) => {
      if (!payload || !payload.store_id) return;
      if (!storeId || String(payload.store_id) !== String(storeId)) return;

      // Si ya tenemos conversación y el mensaje es de otra, ignorar
      if (conversation && String(payload.conversation_id) !== String(conversation.id)) {
        return;
      }

      // Si aún no teníamos conversación asociada en el cliente, usar la del payload
      if (!conversation) {
        setConversation({
          id: payload.conversation_id,
          store_id: payload.store_id,
          type: 'customer'
        });
      }

      setMessages((prev) => [
        ...prev,
        {
          id: payload.id,
          message: payload.message,
          author_type: payload.author_type || 'staff',
          created_at: payload.created_at,
          username: store?.name || 'Tienda',
          isOwn: false,
          metadata: payload.metadata
        }
      ]);
      scrollToBottom();

      // Notificación para pestaña Clientes / botón de chat
      setHasNewClientMsg(true);
      if (typeof onNewCustomerMessage === 'function') {
        onNewCustomerMessage();
      }
    };

    socket.on('store:conversation:staff-message', handleStaffMessage);

    return () => {
      socket.off('store:conversation:staff-message', handleStaffMessage);
    };
  }, [socket, user, storeId, conversation, store, onNewCustomerMessage]);

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!inputMessage.trim() || !user || !storeId || sending) {
      return;
    }

    setSending(true);
    setError(null);

    const messageText = inputMessage.trim();

    try {
      const response = await axios.post(`/api/store/messaging/${storeId}/customer/messages`, {
        message: messageText
      });

      const conv = response?.data?.conversation || null;
      if (conv) {
        setConversation(conv);
      }

      const nowIso = new Date().toISOString();

      setMessages((prev) => [
        ...prev,
        {
          id: `local-${Date.now()}`,
          message: messageText,
          author_type: 'customer',
          created_at: nowIso,
          username: user.display_name || user.username || 'Tú',
          isOwn: true
        }
      ]);

      setInputMessage('');
      scrollToBottom();
    } catch (err) {
      setError(getBackendErrorMessage(err, 'Ocurrió un error al enviar el mensaje.'));
    } finally {
      setSending(false);
    }
  };

  const renderBody = () => {
    if (storeLoading) {
      return (
        <div className="messages-area">
          <div className="empty-messages">
            <p>Cargando tienda...</p>
            <p className="text-sm">Espera un momento.</p>
          </div>
        </div>
      );
    }

    if (staffLoading && isStaff) {
      return (
        <div className="messages-area">
          <div className="empty-messages">
            <p>Cargando información de staff…</p>
          </div>
        </div>
      );
    }

    if (storeError || !storeId) {
      return (
        <div className="messages-area">
          <div className="empty-messages">
            <p>No se pudo cargar la tienda.</p>
            <p className="text-sm">Intenta recargar la página.</p>
          </div>
        </div>
      );
    }

    if (!user) {
      return (
        <div className="messages-area">
          <div className="empty-messages">
            <p>Inicia sesión para chatear con esta tienda</p>
            <p className="text-sm">
              Podrás enviar mensajes al equipo y recibir respuestas aquí mismo.
            </p>
          </div>
        </div>
      );
    }

    // Vista Staff (solo cuando activeStoreTab === 'staff')
    if (isStaff && activeStoreTab === 'staff') {
      return (
        <StoreStaffChatTab
          storeId={storeId}
          storeSlug={storeSlug}
          storeLogo={store?.logo_url}
          storeName={store?.name || 'la tienda'}
          embedded
          onNewIncomingForStaff={() => {
            setHasNewStaffMsg(true);
            if (typeof onNewStaffMessage === 'function') {
              onNewStaffMessage();
            }
          }}
        />
      );
    }

    // Vista Clientes (por defecto)
    return (
      <>
        <div className="messages-area">
          {messages.length === 0 ? (
            <div className="empty-messages">
              <p>Escribe tu primera pregunta para la tienda</p>
              <p className="text-sm">
                Un miembro del equipo de {store?.name || 'la tienda'} te responderá aquí.
              </p>
            </div>
          ) : (
            messages.map((msg) => (
              <ChatMessage
                key={msg.id}
                username={msg.username}
                message={msg.message}
                timestamp={msg.created_at}
                isOwn={!!msg.isOwn || msg.author_type === 'customer'}
                showUsername={true}
                metadata={msg.metadata}
                storeSlug={storeSlug}
                storeLogo={store?.logo_url}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <form className="message-input" onSubmit={handleSendMessage}>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={`Mensaje para ${store?.name || 'la tienda'}...`}
            maxLength={1000}
            disabled={sending}
          />
          <button
            type="submit"
            disabled={sending || !inputMessage.trim() || !user}
          >
            <Send size={18} />
          </button>
        </form>

        {error && (
          <div className="px-3 pb-2 text-xs text-red-500">
            {error}
          </div>
        )}

        {staffError && (
          <div className="px-3 pb-2 text-xs text-red-500">
            {staffError}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="tab-panel">
      <div className="tab-info">
        <span className="tab-title">
          🏬 Chat: "{store?.name || 'Tienda'}"
        </span>

        {isStaff ? (
          <div className="store-chat-subtabs">
            <button
              type="button"
              className={`store-chat-subtab ${
                activeStoreTab === 'staff' ? 'active' : ''
              } ${hasNewStaffMsg && activeStoreTab !== 'staff' ? 'notify-staff' : ''}`}
              onClick={() => {
                setActiveStoreTab('staff');
                setHasNewStaffMsg(false);
              }}
            >
              Clientes
            </button>
            <button
              type="button"
              className={`store-chat-subtab ${
                activeStoreTab === 'clients' ? 'active' : ''
              } ${hasNewClientMsg && activeStoreTab !== 'clients' ? 'notify-client' : ''}`}
              onClick={() => {
                setActiveStoreTab('clients');
                setHasNewClientMsg(false);
              }}
            >
              Staff
            </button>
          </div>
        ) : (
          <span className="tab-subtitle">
            Clientes
          </span>
        )}
      </div>

      {renderBody()}
    </div>
  );
};

export default StoreChatTab;
